// ===========================================
// Prompt Categories - Role + Action System
// ===========================================
// Two-dimensional organization for AI prompts:
// - ROLES: Engineering role perspective (Frontend, Backend, AI Engineer, etc.)
// - ACTIONS: What you want to accomplish (analyze, generate, debug, etc.)
// Tags handle technology-specific filtering (React, Node, etc.)

import {
  // Role Icons - Engineering perspectives
  Layers,       // Full-Stack - end-to-end
  Layout,       // Frontend - UI/client-side
  Server,       // Backend - APIs/server-side
  Bot,          // AI Engineer - LLM/ML
  GitBranch,    // Architecture - system design
  Rocket,       // DevOps - deploy/infra
  Shield,       // Security - auth/protection
  Users,        // Tech Lead - review/standards
  
  // Action Icons - What you want to do
  Search,       // Analyze - understand existing code
  Sparkles,     // Generate - create new code
  RefreshCw,    // Refactor - restructure existing code
  Bug,          // Debug - find and fix issues
  Zap,          // Optimize - improve performance
  TestTube,     // Test - write tests
  FileText,     // Document - add docs/comments
  Eye           // Review - code review feedback
} from 'lucide-react';

// ===========================================
// ROLE CATEGORIES - Engineering Perspectives
// ===========================================
// Primary categorization: "What engineering hat am I wearing?"
// These map to real engineering roles with clear scopes

export const ROLE_CATEGORIES = {
  fullStack: {
    key: 'fullStack',
    label: 'Full-Stack',
    icon: Layers,
    color: '#22c55e',       // Green - creation/building
    description: 'End-to-end features, integration across layers'
  },
  frontend: {
    key: 'frontend',
    label: 'Frontend',
    icon: Layout,
    color: '#ec4899',       // Pink - visual/UI
    description: 'UI components, styling, animations, client-side'
  },
  backend: {
    key: 'backend',
    label: 'Backend',
    icon: Server,
    color: '#3b82f6',       // Blue - logic/data
    description: 'APIs, services, business logic, server-side'
  },
  aiEngineer: {
    key: 'aiEngineer',
    label: 'AI Engineer',
    icon: Bot,
    color: '#00d4ff',       // Cyan - AI/futuristic
    description: 'LLM integration, embeddings, RAG, AI agents'
  },
  architecture: {
    key: 'architecture',
    label: 'Architecture',
    icon: GitBranch,
    color: '#a855f7',       // Purple - high-level thinking
    description: 'System design, patterns, scalability decisions'
  },
  devops: {
    key: 'devops',
    label: 'DevOps',
    icon: Rocket,
    color: '#f97316',       // Orange - deploy/action
    description: 'CI/CD, deployment, infrastructure, monitoring'
  },
  security: {
    key: 'security',
    label: 'Security',
    icon: Shield,
    color: '#ef4444',       // Red - protection/warning
    description: 'Authentication, encryption, vulnerabilities'
  },
  techLead: {
    key: 'techLead',
    label: 'Tech Lead',
    icon: Users,
    color: '#eab308',       // Yellow/Gold - leadership
    description: 'Code review, best practices, technical decisions'
  }
};

// ===========================================
// ACTION CATEGORIES - What you want to do
// ===========================================
// Secondary filter: "What action am I trying to take?"
// Actions are universal across all roles

export const ACTION_CATEGORIES = {
  analyze: {
    key: 'analyze',
    label: 'Analyze',
    icon: Search,
    color: '#3b82f6',       // Blue - investigation/discovery
    description: 'Understand codebase, review architecture, audit quality'
  },
  generate: {
    key: 'generate',
    label: 'Generate',
    icon: Sparkles,
    color: '#22c55e',       // Green - creation/growth
    description: 'Create new features, components, boilerplate'
  },
  refactor: {
    key: 'refactor',
    label: 'Refactor',
    icon: RefreshCw,
    color: '#8b5cf6',       // Purple - transformation
    description: 'Restructure, modernize, apply design patterns'
  },
  debug: {
    key: 'debug',
    label: 'Debug',
    icon: Bug,
    color: '#ef4444',       // Red - errors/problems
    description: 'Find bugs, trace errors, diagnose issues'
  },
  optimize: {
    key: 'optimize',
    label: 'Optimize',
    icon: Zap,
    color: '#f59e0b',       // Amber - speed/performance
    description: 'Performance, caching, bundle size, query tuning'
  },
  test: {
    key: 'test',
    label: 'Test',
    icon: TestTube,
    color: '#f97316',       // Orange - testing
    description: 'Unit, integration, E2E tests, mocking strategies'
  },
  document: {
    key: 'document',
    label: 'Document',
    icon: FileText,
    color: '#14b8a6',       // Teal - documentation
    description: 'READMEs, API docs, inline comments, JSDoc'
  },
  review: {
    key: 'review',
    label: 'Review',
    icon: Eye,
    color: '#64748b',       // Slate - oversight
    description: 'Code review, PR feedback, best practice checks'
  }
};

// ===========================================
// Combined Categories Object (for backwards compatibility)
// ===========================================
// Maps both roles and actions for lookup

export const CATEGORIES = {
  ...ROLE_CATEGORIES,
  ...ACTION_CATEGORIES
};

// ===========================================
// Helper Exports
// ===========================================

// Role arrays for rendering
export const ROLE_CATEGORIES_ARRAY = Object.values(ROLE_CATEGORIES);

// Action arrays for rendering
export const ACTION_CATEGORIES_ARRAY = Object.values(ACTION_CATEGORIES);

// All categories as array (combined for backwards compatibility)
export const CATEGORIES_ARRAY = Object.values(CATEGORIES);

// All valid keys (for validation)
export const ALL_ROLE_KEYS = Object.keys(ROLE_CATEGORIES);
export const ALL_ACTION_KEYS = Object.keys(ACTION_CATEGORIES);
export const ALL_CATEGORY_KEYS = Object.keys(CATEGORIES);

// Default values for new prompts
export const DEFAULT_ROLE = 'fullStack';
export const DEFAULT_ACTION = 'generate';

// Legacy export for backwards compatibility
export const DEFAULT_CATEGORY = 'generate';

// ===========================================
// Migration Mapping - Old categories to new roles
// ===========================================
// Used to migrate existing prompts to the new role system

export const CATEGORY_TO_ROLE_MAP = {
  // Old action categories -> default role
  analyze: 'architecture',
  generate: 'fullStack',
  refactor: 'techLead',
  debug: 'fullStack',
  optimize: 'architecture',
  test: 'techLead',
  document: 'techLead',
  review: 'techLead',
  
  // Old domain categories -> new roles
  appLogic: 'backend',
  aiLlm: 'aiEngineer',
  dataPipelines: 'backend',
  apisIntegration: 'backend',
  uiComponents: 'frontend',
  security: 'security',
  devops: 'devops',
  database: 'backend',
  
  // Legacy categories
  frontend: 'frontend',
  api: 'backend',
  logic: 'backend',
  data: 'backend',
  auth: 'security',
  testing: 'techLead',
  ai: 'aiEngineer',
  general: 'fullStack'
};

// ===========================================
// Legacy exports for backwards compatibility
// ===========================================
// These maintain compatibility with existing code that imports old names

export const DOMAIN_CATEGORIES = ROLE_CATEGORIES;
export const DOMAIN_CATEGORIES_ARRAY = ROLE_CATEGORIES_ARRAY;
